import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '@/hooks/useI18n';
import { useChartTooltipTrigger } from '@/hooks/useChartTooltipTrigger';
import { formatInt, formatPct } from '@/lib/format';
import type { AnalyticsInsightModel, ConcentrationPoint } from '@/lib/analyticsInsights';

export function ConcentrationChart({ model }: { model: AnalyticsInsightModel }) {
  const { t } = useI18n();
  const tooltipTrigger = useChartTooltipTrigger();
  const selectedPercent = (model.selectedShare ?? 0) * 100;
  const ticks = [...new Set([0, Math.round(model.accounts.length / 2), model.accounts.length])];
  return (
    <div className="min-w-0 p-4 sm:px-6 sm:py-5">
      <h3 className="text-[13px] font-semibold text-text-1">
        {t('Comment les vues se répartissent', 'How views are distributed')}
      </h3>
      <div
        className="mt-3 h-[210px] min-w-0 sm:h-[230px]"
        role="img"
        aria-label={t(
          `${model.selected.length} comptes sur ${model.accounts.length} représentent ${formatPct(model.selectedShare, { sign: false, digits: 1 })} des vues gagnées.`,
          `${model.selected.length} of ${model.accounts.length} accounts represent ${formatPct(model.selectedShare, { sign: false, digits: 1 })} of views gained.`,
        )}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={model.points}
            margin={{ top: 12, right: 12, bottom: 0, left: -16 }}
            accessibilityLayer
          >
            <CartesianGrid vertical={false} stroke="hsl(var(--line-1))" />
            <XAxis
              dataKey="count"
              type="number"
              domain={[0, model.accounts.length]}
              ticks={ticks}
              tickFormatter={formatInt}
              tick={{ fill: 'hsl(var(--text-3))', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              minTickGap={24}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 50, 100]}
              tickFormatter={(value: number) => `${value}%`}
              tick={{ fill: 'hsl(var(--text-3))', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={55}
            />
            <ReferenceArea
              x1={0}
              x2={model.selected.length}
              fill="hsl(var(--violet-soft))"
              fillOpacity={0.8}
              strokeOpacity={0}
            />
            <Line
              dataKey="equalShare"
              stroke="hsl(var(--line-3))"
              strokeWidth={1}
              strokeDasharray="4 4"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
            <Line
              dataKey="viewsShare"
              type="linear"
              stroke="hsl(var(--violet))"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(var(--bg-1))' }}
              isAnimationActive={false}
            />
            <ReferenceDot
              x={model.selected.length}
              y={selectedPercent}
              r={5}
              fill="hsl(var(--violet))"
              stroke="hsl(var(--bg-1))"
              strokeWidth={2}
            />
            <Tooltip trigger={tooltipTrigger} content={<ConcentrationTooltip />} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-[11px] text-text-3">
        <span>
          {t('Comptes classés du plus au moins contributeur', 'Accounts ranked by contribution')}
        </span>
        <span>{t('Pointillés : répartition égale', 'Dashed: equal distribution')}</span>
      </div>
    </div>
  );
}

function ConcentrationTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: ConcentrationPoint }>;
}) {
  const { t } = useI18n();
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div className="rounded-[10px] border border-line-1 bg-bg-1 px-3 py-2 text-[12px] shadow-card">
      <p className="font-medium text-text-1">
        {formatInt(point.count)} {t('comptes', 'accounts')}
      </p>
      <p className="mt-1 text-violet-deep">
        {formatPct(point.viewsShare / 100, { sign: false, digits: 1 })}{' '}
        {t('des vues gagnées', 'of views gained')}
      </p>
    </div>
  );
}
